import { Button, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography } from "@material-ui/core";
import React, { FC, ReactElement, useEffect, useState } from "react";
import { makeStyles } from '@material-ui/core/styles';
import { Source, SourceField } from "../../models/Source";
import SourcesService from "../../services/SourcesServices";
import SourceConnectionService from "../../services/SourcesConnectionService";
import { useHistory, useParams } from "react-router";
import { SourceConnection } from "../../models/SourceConnection";
import { Alert } from "@material-ui/lab";
import { apiErrorMessage } from "../../utils/apiError";

const useStyles = makeStyles((theme) => ({
    button: {
        margin: theme.spacing(1),
        backgroundColor: '#349CC9',
        color: 'white'
    },
    root: {
        '& > *': {
            margin: theme.spacing(1),
            width: '25ch',
        },
    },
    formControl: {
        margin: theme.spacing(1),
        minWidth: 120,
        width: '100%'
    },
    fullWidthField: {
        width: '100%'
    },
    pemFieldWrap: {
        width: '100%',
        maxWidth: 720,
    },
}));

const SNOWFLAKE_PRIVATE_KEY_HELP =
    'Paste the full .p8 file (all lines). Leave pass empty. Use private_key_pass only if the header says ENCRYPTED.';

const SourceConnectionEditView: FC = (): ReactElement => {
    const { getSourceConnection, save } = SourceConnectionService();
    const { getSources } = SourcesService();
    const { id } = useParams<{ id?: string }>();
    const classes = useStyles();
    const history = useHistory();

    const [sources, setSources] = useState<Source[]>();
    const [sourceConnection, setSourceConnection] = useState<SourceConnection>();
    const [name, setName] = useState('');
    const [selectedSource, setSelectedSource] = useState<Source>();
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string>();

    const getId = () => (id ? Number.parseInt(id, 10) : 0);

    const loadSources = (connection?: SourceConnection) => {
        if (connection) {
            setSourceConnection(connection);
            setName(connection.name);
            setSelectedSource({
                ...connection.source,
                fields: connection.source.fields.map((f) => ({
                    ...f,
                    value: connection.fields.find((sf) => sf.sourceField.id === f.id)?.value ?? '',
                })),
            });
        }
        getSources().then((res) => {
            let list = res;
            if (connection) {
                list = list.map((rs) => ({
                    ...rs,
                    fields: rs.fields.map((rf) => ({
                        ...rf,
                        value: connection.fields?.find((scf) => scf.sourceField.id === rf.id)?.value ?? '',
                    })),
                }));
            }
            setSources(list);
        });
    };

    useEffect(() => {
        if (id) {
            getSourceConnection(getId()).then((connection) => loadSources(connection));
        } else {
            loadSources();
        }
    }, []);

    const normalizeFieldValue = (v: unknown): string => {
        if (v == null) return '';
        return typeof v === 'string' ? v.trim() : String(v).trim();
    };

    const renderField = (s: SourceField) => {
        const isSnowflake = selectedSource?.name === 'Snowflake';
        const nameLower = (s.name || '').toLowerCase();
        const isPrivateKey = nameLower === 'private_key';
        const isPass = nameLower === 'pass';

        const onChange = (val: string) => {
            setSelectedSource({
                ...selectedSource!,
                fields: selectedSource!.fields.map((f) => (f.id === s.id ? { ...f, value: val } : f)),
            });
        };

        if (isPrivateKey) {
            return (
                <div key={s.id} className={classes.pemFieldWrap}>
                    <TextField
                        className={classes.fullWidthField}
                        label={s.placeholder}
                        variant="outlined"
                        value={s.value ?? ''}
                        multiline
                        minRows={10}
                        maxRows={28}
                        fullWidth
                        spellCheck={false}
                        onChange={(e) => onChange(e.target.value)}
                        InputProps={{
                            style: {
                                fontFamily: 'ui-monospace, Menlo, Monaco, Consolas, monospace',
                                fontSize: 13,
                            },
                        }}
                    />
                    <Alert severity="info" style={{ marginTop: 8 }}>{SNOWFLAKE_PRIVATE_KEY_HELP}</Alert>
                </div>
            );
        }

        const helperText =
            isPass && isSnowflake
                ? 'Leave completely empty for key-pair auth (NIRVANA_SERVICE). Any value uses password login instead.'
                : undefined;

        return (
            <TextField
                key={s.id}
                className={classes.fullWidthField}
                label={s.placeholder}
                variant="outlined"
                value={s.value ?? ''}
                type={s.isProtected && !isPrivateKey ? 'password' : 'text'}
                onChange={(e) => onChange(e.target.value)}
                helperText={helperText}
            />
        );
    };

    return (
        <div>
            <Grid container spacing={3}>
                <Grid item xs={8}>
                    <form className={classes.root} noValidate autoComplete="off">
                        <FormControl variant="outlined" className={classes.formControl}>
                            <InputLabel id="connector-label">Connector</InputLabel>
                            {sources && (
                                <Select
                                    labelId="connector-label"
                                    value={selectedSource ? selectedSource.id : ''}
                                    onChange={(e) =>
                                        setSelectedSource(sources.find((s) => s.id === e.target.value))
                                    }
                                    label="Connector"
                                >
                                    {sources.map((s) => (
                                        <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                                    ))}
                                </Select>
                            )}
                        </FormControl>
                        <TextField
                            className={classes.fullWidthField}
                            label="Connection Name"
                            variant="outlined"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                        {selectedSource?.name === 'Snowflake' && (
                            <Alert severity="info" style={{ maxWidth: 720 }}>
                                PMP: use <strong>NIRVANA_SERVICE</strong>, role <strong>NIRVANA_CAMEL_ROLE</strong>,
                                warehouse <strong>LOAD_WH</strong>, database <strong>PMP_DATALAKE_PROD</strong>.
                                Copy account from the working PMP Snowflake connection; only user + private_key differ.
                            </Alert>
                        )}
                        {selectedSource?.fields
                            .slice()
                            .sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || a.id - b.id)
                            .map((s) => renderField(s))}
                    </form>
                </Grid>
            </Grid>
            {saveError && (
                <Alert severity="error" style={{ marginTop: 8, maxWidth: 720 }} onClose={() => setSaveError(undefined)}>
                    {saveError}
                </Alert>
            )}
            <Button
                variant="contained"
                className={classes.button}
                disabled={saving || !name.trim() || !selectedSource}
                onClick={() => {
                    if (!selectedSource) {
                        setSaveError('Select a connector type.');
                        return;
                    }
                    setSaving(true);
                    setSaveError(undefined);
                    save({
                        id: getId(),
                        sourceId: selectedSource.id,
                        fields: selectedSource.fields.map((f) => {
                            const existing = sourceConnection?.fields?.find((sf) => sf.sourceField.id === f.id);
                            return {
                                id: existing ? existing.id : 0,
                                sourceFieldId: f.id,
                                value: normalizeFieldValue(f.value),
                            };
                        }),
                        name: name.trim(),
                    })
                        .then((res) => {
                            if (typeof res === 'string' && /error/i.test(res)) {
                                setSaveError(res);
                                return;
                            }
                            history.push('/source-connections');
                        })
                        .catch((err) => setSaveError(apiErrorMessage(err)))
                        .finally(() => setSaving(false));
                }}
            >
                {saving ? 'Saving…' : 'Save'}
            </Button>
        </div>
    );
};

export default SourceConnectionEditView;
